"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.graphqlOperation = exports.API = exports.Auth = exports.initializeAmplify = void 0;
const api_1 = require("@aws-amplify/api");
const auth_1 = require("@aws-amplify/auth");
const amplifyConfig_1 = require("./amplifyConfig");
const environment_1 = require("./environment");
const useLogger_1 = require("../hooks/useLogger");
const logger = (0, useLogger_1.useLogger)('amplifyBootstrap');
let isAmplifyInitialized = false;
let apiClient = (0, api_1.generateClient)();
function initializeAmplify() {
    if (isAmplifyInitialized) {
        logger.warn('Amplify already initialized');
        return apiClient;
    }
    (0, amplifyConfig_1.configureAmplify)();
    isAmplifyInitialized = true;
    // @ts-ignore
    apiClient = (0, api_1.generateClient)();
    return apiClient;
}
exports.initializeAmplify = initializeAmplify;
initializeAmplify();
const AuthModule = {
    signInWithRedirect: auth_1.signInWithRedirect,
    signIn: async (params) => (0, auth_1.signIn)(params),
    confirmSignIn: async (params) => (0, auth_1.confirmSignIn)(params),
    signOut: async () => {
        try {
            return await (0, auth_1.signOut)();
        }
        catch (error) {
            logger.error('Error in signOut:', { error });
            throw error;
        }
    },
    currentAuthenticatedUser: async () => {
        try {
            const user = await (0, auth_1.getCurrentUser)();
            return user;
        }
        catch (error) {
            logger.error('Error in currentAuthenticatedUser:', { error });
            throw error;
        }
    },
    currentSession: async (forceRefresh) => {
        try {
            const session = await (0, auth_1.fetchAuthSession)({ forceRefresh });
            return session;
        }
        catch (error) {
            logger.error('Error in currentSession:', { error });
            throw error;
        }
    },
    fetchUserAttributes: async () => {
        try {
            const attributes = await (0, auth_1.fetchUserAttributes)();
            return attributes;
        }
        catch (error) {
            logger.error('Error in fetchUserAttributes:', { error });
            throw error;
        }
    },
};
const MFAModule = {
    getPreferredMFA: async (_user) => 'NOMFA',
    setPreferredMFA: async (user, mfaMethod) => {
        try {
            if (user && typeof user.setPreferredMFA === 'function') {
                return await user.setPreferredMFA(mfaMethod);
            }
            return { success: false, message: 'MFA preference setting not implemented' };
        }
        catch (error) {
            logger.error('Error setting preferred MFA:', { error });
            throw error;
        }
    },
};
exports.Auth = {
    signInWithRedirect: AuthModule.signInWithRedirect,
    fetchUserAttributes: AuthModule.fetchUserAttributes,
    signIn: async (username, password) => {
        try {
            const signInOutput = await (0, auth_1.signIn)({
                username,
                password,
            });
            const result = {
                isSignedIn: signInOutput.isSignedIn,
            };
            if (signInOutput.nextStep) {
                result.nextStep = {
                    signInStep: signInOutput.nextStep.signInStep,
                };
                const codeDetails = signInOutput.nextStep.codeDeliveryDetails;
                if (codeDetails) {
                    result.nextStep.codeDeliveryDetails = {
                        deliveryMedium: codeDetails.deliveryMedium,
                        destination: codeDetails.destination,
                        attributeName: codeDetails.attributeName,
                    };
                }
            }
            return result;
        }
        catch (err) {
            logger.error('Error in signIn:', { err });
            const error = err instanceof Error ? err : new Error(String(err));
            throw error;
        }
    },
    confirmSignIn: async (params) => {
        try {
            const result = await (0, auth_1.confirmSignIn)(params);
            return result;
        }
        catch (error) {
            logger.error('Error in confirmSignIn:', { error });
            throw error;
        }
    },
    signOut: AuthModule.signOut,
    currentAuthenticatedUser: AuthModule.currentAuthenticatedUser,
    currentSession: AuthModule.currentSession,
    signUp: async (username, password, attributes) => {
        try {
            const result = await (0, auth_1.signUp)({
                username,
                password,
                options: {
                    userAttributes: attributes,
                },
            });
            return result;
        }
        catch (error) {
            logger.error('Error in signUp:', { error });
            throw error;
        }
    },
    confirmSignUp: async (username, code) => {
        try {
            const result = await (0, auth_1.confirmSignUp)({
                username,
                confirmationCode: code,
            });
            return result;
        }
        catch (error) {
            logger.error('Error in confirmSignUp:', { error });
            throw error;
        }
    },
    forgotPassword: async (username) => {
        try {
            const result = await (0, auth_1.resetPassword)({
                username,
            });
            return result;
        }
        catch (error) {
            logger.error('Error in forgotPassword:', { error });
            throw error;
        }
    },
    forgotPasswordSubmit: async (username, code, newPassword) => {
        try {
            const result = await (0, auth_1.confirmResetPassword)({
                username,
                confirmationCode: code,
                newPassword,
            });
            return result;
        }
        catch (error) {
            logger.error('Error in forgotPasswordSubmit:', { error });
            throw error;
        }
    },
    getPreferredMFA: MFAModule.getPreferredMFA,
    setPreferredMFA: MFAModule.setPreferredMFA,
};
const API = Object.assign({}, apiClient, {
    post: async (path, init) => {
        const apiGatewayUrl = (0, environment_1.getApiGatewayUrl)();
        const fullUrl = apiGatewayUrl
            ? path.startsWith('/')
                ? `${apiGatewayUrl}${path}`
                : `${apiGatewayUrl}/${path}`
            : path;
        const response = await fetch(fullUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...init?.headers,
            },
            body: JSON.stringify(init?.body),
        });
        return response.json();
    },
    get: async (path, init) => {
        const apiGatewayUrl = (0, environment_1.getApiGatewayUrl)();
        const fullUrl = apiGatewayUrl
            ? path.startsWith('/')
                ? `${apiGatewayUrl}${path}`
                : `${apiGatewayUrl}/${path}`
            : path;
        logger.debug(`Making API GET request to: ${fullUrl}`);
        const response = await fetch(fullUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...init?.headers,
            },
        });
        return response.json();
    },
});
exports.API = API;
const graphqlOperation = (query, variables) => ({
    query,
    variables,
});
exports.graphqlOperation = graphqlOperation;
exports.default = apiClient;
